import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, EMPTY, finalize, map, Observable, throwError } from 'rxjs';
import { AppData, AuthData } from './data';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {

  constructor(
    private errorHandler: ErrorHandler,
    private ngZone: NgZone,
    private appService: AppData,
    private authService: AuthData,
    private messageService: MessageService,
    private router: Router
  ) {}

  // Strong, normalized self-care URL detector
  private isSelfcareRequest(url: string): boolean {
    if (!url || !environment.selfCareUrl) return false;

    const reqUrl = url.toLowerCase().trim();
    const baseUrl = environment.selfCareUrl.toLowerCase().trim().replace(/\/+$/, '');

    return reqUrl.startsWith(baseUrl);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    this.appService.showLoader();

    const apiReq = req.clone({
      setHeaders: req.url.includes('assets/')
        ? {}
        : { Authorization: `Bearer ${this.authService.authorizationToken()}` }
    });

    return next.handle(apiReq).pipe(

      // Handle success=false but 200 OK responses
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body = event.body || {};
          if (!event.url.includes('assets/') && body.success === false) {
            return event.clone({
              body: { ...body, __handledError: true }
            });
          }
        }
        return event;
      }),

      catchError((error: HttpErrorResponse) => {
        this.appService.hideLoader();

        const isSelfcare = this.isSelfcareRequest(req.url);

        console.log("REQUEST URL:", req.url);
        console.log("MATCH SELFCARE:", isSelfcare);
        console.log("ERROR STATUS:", error.status);

        // 🔥 FIX: Treat BOTH 401 AND status 0 as unauthorized for self-care API
        if (isSelfcare && (error.status === 401 || error.status === 0)) {

          sessionStorage.clear();
          localStorage.clear();

          this.ngZone.run(() => {
            this.messageService.add({
              severity: 'error',
              summary: 'Account Suspended',
              detail: 'Your account has been suspended by admin. Please contact your admin.',
              life: 5000
            });

            this.router.navigate(['/login']);
          });

          return EMPTY; // prevent GlobalErrorHandler from firing
        }

        // 🔥 ALL OTHER APIs: 401 = Session expired → Redirect to login
        if (!isSelfcare && error.status === 401) {
          this.ngZone.run(() => {
            this.router.navigate(['/login']);
          });
          return EMPTY;
        }

        let errorMessage = '';

        if (error.status === 413) {
          errorMessage = "The file you're trying to upload is too large. Please try a smaller file.";
        } else {
          const pickMessage = (body: any) => isSelfcare
            ? (body?.responseMessage || body?.message || '')
            : (body?.responseMessage_en || body?.message || '');

          if (typeof error.error === 'string') {
            try {
              errorMessage = pickMessage(JSON.parse(error.error));
            } catch {
              errorMessage = error.error;
            }
          } else if (error.error && typeof error.error === 'object') {
            errorMessage = pickMessage(error.error);
          }

          if (!errorMessage) {
            errorMessage =
              'There might be some Technical Error or Connectivity Problem';
          }
        }

        const customError = new FatalError(errorMessage, error.status);
        // 🚫 Don't log handled self-care errors or expected 404s (component handles those)
        if (!(isSelfcare && (error.status === 401 || error.status === 0)) && error.status !== 404) {
          this.errorHandler.handleError(customError);
        }

        return throwError(() => customError);
      }),

      finalize(() => {
        this.appService.hideLoader();
      })
    );
  }
}

// ------------------ CUSTOM ERROR ---------------------

export class FatalError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'CustomError';
    this.statusCode = statusCode;
  }
}
